import { useState } from "react";
import { Anchor, ExternalLink, Loader2 } from "lucide-react";
import { toast } from "sonner";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { Button } from "@/components/ui/button";
import { type AnchorTriggerResponse, triggerCaseAnchor } from "@/lib/api";
import { classifyApiError } from "@/hooks/useApiErrorState";

const MARINE_DEEP = "oklch(0.115 0.038 243)";
const GOLD = "oklch(0.695 0.105 68)";

interface AnchorTriggerDialogProps {
  caseId: number;
  disabled?: boolean;
  onTriggered: (response: AnchorTriggerResponse) => void;
}

// Modale de confirmation pour POST /cases/{id}/anchor.
//
// Geste de DIRECTION arbitrale : irreversible (la racine Merkle est
// publiee on-chain et ne peut plus etre retiree), consomme du gas
// Polygon en mainnet (~ 0.01 MATIC), et bloque par le contrat si la
// meme racine a deja ete ancree (BlockchainServiceError -> 503 cote
// API). On force donc :
// 1. Une AlertDialog avec texte explicite sur l'irreversibilite.
// 2. Une note sur les couts.
// 3. Si l'API retourne une erreur (ex. profil non verifie), on
//    affiche un toast d'erreur au lieu de bloquer toute la page.
export function AnchorTriggerDialog({
  caseId,
  disabled,
  onTriggered,
}: AnchorTriggerDialogProps) {
  const [submitting, setSubmitting] = useState(false);
  const [open, setOpen] = useState(false);

  const onConfirm = async () => {
    if (submitting) return;
    setSubmitting(true);
    try {
      const result = await triggerCaseAnchor(caseId);
      toast.success(
        `Ancrage lance. Tx : ${result.tx_hash.slice(0, 10)}…`,
      );
      onTriggered(result);
      setOpen(false);
    } catch (err) {
      const classified = classifyApiError(err);
      if (classified.kind !== "none") {
        toast.error(classified.detail);
      }
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <AlertDialog open={open} onOpenChange={setOpen}>
      <AlertDialogTrigger asChild>
        <Button
          size="sm"
          disabled={disabled}
          style={{ background: GOLD, color: MARINE_DEEP }}
        >
          <Anchor size={14} className="mr-2" />
          Lancer l'ancrage Polygon
        </Button>
      </AlertDialogTrigger>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle
            className="flex items-center gap-2"
            style={{ color: "oklch(0.115 0.038 243)" }}
          >
            <Anchor size={18} />
            Confirmer l'ancrage on-chain
          </AlertDialogTitle>
          <AlertDialogDescription asChild>
            <div className="space-y-3 text-sm">
              <p>
                Vous etes sur le point de publier la racine Merkle
                courante du dossier <strong>#{caseId}</strong> sur la
                blockchain Polygon. Cette publication :
              </p>
              <ul className="list-disc pl-5 space-y-1">
                <li>
                  est <strong>irreversible</strong> -- une fois la
                  transaction minee, la racine reste publiquement
                  consultable indefiniment ;
                </li>
                <li>
                  consomme du <strong>gas Polygon</strong> (env. 0.01
                  MATIC en mainnet, gratuit en simulation) ;
                </li>
                <li>
                  fige l'état probatoire du dossier au moment T -- les
                  pièces ajoutées après ne seront PAS couvertes par cet
                  ancrage (un nouvel ancrage sera nécessaire).
                </li>
              </ul>
              <p
                className="flex items-center gap-1 text-xs"
                style={{ color: GOLD }}
              >
                <ExternalLink size={11} />
                Le tx_hash sera traçable sur Polygonscan après exécution.
              </p>
            </div>
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel disabled={submitting}>Annuler</AlertDialogCancel>
          <AlertDialogAction
            onClick={(e) => {
              e.preventDefault();
              void onConfirm();
            }}
            disabled={submitting}
            style={{ background: GOLD, color: MARINE_DEEP }}
          >
            {submitting ? (
              <>
                <Loader2 size={14} className="animate-spin mr-2" />
                Envoi tx...
              </>
            ) : (
              "Confirmer l'ancrage"
            )}
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}
